import { Box, Grid, Typography, Checkbox } from '@mui/material';
import { useState } from 'react';
import MultiSelectDropDown from './MultiSelectDropDown';
import { useMediaQuery } from 'react-responsive';
import ReportTableMobile from './ReportTableMobile';
import ReportTable from './ReportTable';
import { ReportData } from '../../model/reportData';
import LoadingButton from '@mui/lab/LoadingButton';
import { ApiStatus } from '../../api/apiStatus';
import { ReportTableType } from '../../model/reportTableType';
import { exportToCSV } from '../../services/exportReportService';
import styles from './FormsAndReport.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';

interface IProps {
  reportData: ReportData[];
  loaded: ApiStatus;
  getReportDataByFilter: Function;
  reportLabel: string;
  tableName: ReportTableType;
  quarantineFix?: boolean;
  setOptions: Function;
  selectedEventId?: (eventId: string) => void;
  areaCode: string[];
}

function ReportContainer({
                           reportData,
                           loaded,
                           getReportDataByFilter,
                           reportLabel,
                           tableName,
                           quarantineFix,
                           setOptions,
                           selectedEventId,
                           areaCode
                         }: IProps) {
  const [optionsSelected, setOptionsSelected] = useState<string[]>([]);
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 900px)' });
  const [checked, setChecked] = useState(false);
  const [resolved, setResolved] = useState(false);


  const handleOptionsSelected = (options: string[]) => {
    setOptionsSelected(options);
    setOptions(options);
  };

  const runReport = () => {
    getReportDataByFilter(checked, resolved);
  };

  const toggleCheckBox = () => {
    setChecked(!checked);
  };

  const showResolved = () => {
    setResolved(!resolved);
  };

  const exportReport = () => {
    exportToCSV(reportData, tableName);
  };

  const notFoundMessages = (tableType:ReportTableType) => {
    if (tableType === ReportTableType.EVENT) {
      return 'There are no open events that meet the criteria'
    } else if(tableType === ReportTableType.NOT_UPLOADED_EVENT) {
      return 'There are no events that meet the criteria'
    } else if(tableType === ReportTableType.PICTURE_DAY) {
      return 'There are no picture day events that meet the criteria'
    } else if(tableType === ReportTableType.EM_COMPLETION_STATUS) {
      return 'There are no EM completed events that meet the criteria'
    } else if(tableType === ReportTableType.QUARANTINE_EVENT) {
      return 'There are no quarantine events that meet the criteria'
    }
  };

  return (
    <>
      {!isTabletOrMobile && (
        <Grid container alignItems="center" justifyContent="flex-start" spacing={1}>
          <Grid item md={tableName === ReportTableType.QUARANTINE_EVENT ? 3 : tableName === ReportTableType.EM_COMPLETION_STATUS ? 6: 4}>
            <Typography variant="h6">{reportLabel}</Typography>
            {(tableName === ReportTableType.EM_COMPLETION_STATUS) && (
              <Typography variant="body2">
                This report shows the Event Manager completion status for events that ends today. Multiple day events
                will only display on the last day of the event.
              </Typography>)}
          </Grid>

          <Grid item md={2}>
            <MultiSelectDropDown
              optionsData={areaCode}
              inputLabel={'Filter For Area'}
              selectedOptions={handleOptionsSelected}
              tableName={tableName}
              areaCode={areaCode}
            />
          </Grid>
          {(tableName !== ReportTableType.PICTURE_DAY && tableName !== ReportTableType.QUARANTINE_EVENT  && tableName !== ReportTableType.EM_COMPLETION_STATUS) && (
            <Grid item m={1} className={styles.includeCheckBox}>
              <Checkbox
                sx={{ float: 'left', p: 0 }}
                disabled={optionsSelected.length <= 0}
                checked={checked}
                onChange={toggleCheckBox}
              />
              <Typography sx={{ float: 'left', pl: 1, display: 'flex', alignItems: 'center', fontSize: 'smaller' }}>
                {'Include events 31-180 days past'}
              </Typography>
            </Grid>
          )}
          {tableName === ReportTableType.QUARANTINE_EVENT && (
            <Grid item m={1} className={styles.includeCheckBox}>
              <Checkbox
                sx={{ float: 'left', p: 0 }}
                disabled={optionsSelected.length <= 0}
                checked={resolved}
                onChange={showResolved}
              />
              <Typography sx={{ float: 'left', pl: 1, display: 'flex', alignItems: 'center', fontSize: 'smaller' }}>
                {'Show Resolved'}
              </Typography>
            </Grid>
          )}
          <Grid item container md={2}>
            <LoadingButton
              fullWidth
              disabled={optionsSelected.length <= 0}
              onClick={runReport}
              sx={{ flexGrow: 1 }}
              variant="contained"
              type="submit"
              loading={loaded === 'LOADING'}
            >
              Run Report
            </LoadingButton>
          </Grid>
          {loaded === 'SUCCESS' && reportData.length > 0 && (
            <Grid item m={1}>
              <FontAwesomeIcon
                icon={faDownload}
                className={styles.exportIcon}
                data-testid="exportIcon"
                onClick={exportReport}
              />
            </Grid>
          )}
        </Grid>
      )}
      {isTabletOrMobile && (
        <Grid container alignItems="center" justifyContent="flex-start" spacing={1}>
          <Grid item sm={12} xs={12} sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ float: 'left' }}>
              {reportLabel}
            </Typography>
          </Grid>
          <Grid item sm={12} xs={12}>
            <MultiSelectDropDown
              optionsData={areaCode}
              inputLabel={'Filter For Area'}
              selectedOptions={handleOptionsSelected}
              tableName={tableName}
              areaCode={areaCode}
            />
          </Grid>
          {(tableName !== ReportTableType.PICTURE_DAY && tableName !== ReportTableType.QUARANTINE_EVENT && tableName !== ReportTableType.EM_COMPLETION_STATUS) && (
            <Grid item container xs={12} sm={12} className={styles.includeCheckBox}>
              {optionsSelected && (
                <Checkbox
                  sx={{ float: 'left', pl: 0 }}
                  disabled={optionsSelected.length <= 0}
                  checked={checked}
                  onChange={toggleCheckBox}
                />
              )}
              {optionsSelected && (
                <Typography
                  sx={{ float: 'left', fontSize: '12px', alignItems: 'center', display: 'flex', fontWeight: 10 }}
                >
                  {'Include events 31-180 days past'}
                </Typography>
              )}
            </Grid>
          )}
          {tableName === ReportTableType.QUARANTINE_EVENT && (
            <Grid item container xs={7} sm={7} className={styles.includeCheckBox}>
              {optionsSelected && (
                <Checkbox
                  sx={{ float: 'left', pl: 2 }}
                  disabled={optionsSelected.length <= 0}
                  checked={resolved}
                  onChange={showResolved}
                />
              )}
              {optionsSelected && (
                <Typography
                  sx={{ float: 'left', fontSize: '12px', alignItems: 'center', display: 'flex', fontWeight: 10 }}
                >
                  {'Show Resolved'}
                </Typography>
              )}
            </Grid>
          )}
          {optionsSelected && tableName !== ReportTableType.PICTURE_DAY && (
            <Grid item xs={3} sm={3}>
              <LoadingButton
                disabled={optionsSelected.length <= 0}
                sx={{ ml: 1 }}
                onClick={runReport}
                variant="contained"
                type="submit"
                loading={loaded === 'LOADING'}
                size="medium"
              >
                Report
              </LoadingButton>
            </Grid>
          )}
          {optionsSelected && tableName === ReportTableType.PICTURE_DAY && (
            <Grid item xs={5} sm={5}>
              <LoadingButton
                sx={{ ml: 2 }}
                disabled={optionsSelected.length <= 0}
                onClick={runReport}
                variant="contained"
                type="submit"
                loading={loaded === 'LOADING'}
                size="medium"
              >
                Report
              </LoadingButton>
            </Grid>
          )}
          {loaded === 'SUCCESS' && tableName !== ReportTableType.PICTURE_DAY && reportData.length > 0 && (
            <Grid item xs={5} sm={5}>
              <FontAwesomeIcon
                icon={faDownload}
                className={styles.exportIcon}
                style={{ marginLeft: '2em' }}
                data-testid="exportIcon"
                onClick={exportReport}
              />
            </Grid>
          )}
          {loaded === 'SUCCESS' && tableName === ReportTableType.PICTURE_DAY && reportData.length > 0 && (
            <Grid item xs={2} sm={2}>
              <FontAwesomeIcon
                icon={faDownload}
                className={styles.exportIcon}
                style={{ marginLeft: '0em' }}
                data-testid="exportIcon"
                onClick={exportReport}
              />
            </Grid>
          )}
        </Grid>
      )}
      <Box sx={{ textAlign: 'left', mt: '20px' }}>
        {loaded === 'SUCCESS' &&
          (reportData.length !== 0 ? (
            isTabletOrMobile ? (
              <ReportTableMobile selectedEventId={selectedEventId} tableData={reportData} tableName={tableName}
                                 quarantineFix={quarantineFix} />
            ) : (
              <ReportTable selectedEventId={selectedEventId} tableName={tableName} tableData={reportData}
                           quarantineFix={quarantineFix} />
            )
          ) : (
            <Typography>{notFoundMessages(tableName)}</Typography>
          ))}
      </Box>
      {loaded === 'FAILED' && (
        <Box sx={{ textAlign: 'left', mt: '20px' }}>
          <Typography>Not able to load report</Typography>
        </Box>
      )}
    </>
  );
}

export default ReportContainer;
